/**
 * Plafond `limit` des listes parcourables de l'API v2
 * (``BROWSABLE_MAX_LIMIT`` côté backend) : au-delà, l'API renvoie 422.
 */
export const MAX_PAGE_LIMIT = 1000;

export class ApiError extends Error {
  readonly apiErrors: unknown;
  /** HTTP status of the failed response (when available). Used e.g. pour
   * décider de ne pas retenter une erreur client (4xx). */
  readonly status?: number;

  constructor(errors: unknown, status?: number) {
    super(ApiError.formatMessage(errors));
    this.name = "ApiError";
    this.apiErrors = errors;
    this.status = status;
  }

  hasFieldError(field: string): boolean {
    return Boolean(
      this.apiErrors &&
        typeof this.apiErrors === "object" &&
        field in (this.apiErrors as Record<string, unknown>),
    );
  }

  private static formatMessage(errors: unknown): string {
    if (typeof errors === "string") return errors;
    if (!errors || typeof errors !== "object") return "Une erreur est survenue";

    const errObj = errors as Record<string, unknown>;

    if ("detail" in errObj) {
      const detail = errObj.detail;
      // django-ninja renvoie un 422 avec `detail` = liste d'objets pydantic
      // ({ loc, msg, type }). `String([...])` donnerait « [object Object] ».
      if (Array.isArray(detail)) {
        return detail
          .map((item) => {
            if (item && typeof item === "object" && "msg" in item) {
              const { loc, msg } = item as { loc?: unknown[]; msg?: unknown };
              const field = Array.isArray(loc) ? loc.slice(1).join(".") : "";
              return field ? `${field} : ${msg}` : String(msg);
            }
            return String(item);
          })
          .join(" | ");
      }
      return String(detail);
    }
    if ("non_field_errors" in errObj) {
      const msgs = errObj.non_field_errors;
      return Array.isArray(msgs) ? msgs.join(", ") : String(msgs);
    }

    return Object.entries(errObj)
      .map(([key, value]) => {
        const msgs = Array.isArray(value) ? value.join(", ") : String(value);
        return `${key} : ${msgs}`;
      })
      .join(" | ");
  }
}

export const apiCall = async <T>(
  result: Promise<{ data?: T; error?: unknown; response?: Response }>,
): Promise<T> => {
  const r = await result;
  if (r.error !== undefined) throw new ApiError(r.error, r.response?.status);
  warnOnTruncatedList(r.data, r.response);
  return r.data as T;
};

/** Params qui signent un appel qui pagine volontairement. */
const PAGINATION_PARAMS = ["limit", "offset", "page", "page_size"];

/**
 * Une liste paginée demandée **sans paramètre de page** et dont `items` est
 * plus court que `count` a été tronquée en silence : soit l'appel doit paginer
 * (et afficher `<sgdf-pagination>`), soit la liste n'aurait pas dû être
 * paginée côté API. Invisible en prod, donc on le dit en dev — c'est le bug
 * qui se diagnostique le plus mal (« il me manque des participants »).
 */
function warnOnTruncatedList(data: unknown, response?: Response): void {
  if (!import.meta.dev) return;
  if (!data || typeof data !== "object") return;
  const paged = data as { items?: unknown; count?: unknown };
  if (!Array.isArray(paged.items) || typeof paged.count !== "number") return;
  if (paged.items.length >= paged.count) return;
  if (!response?.url) return;
  const query = new URL(response.url).searchParams;
  if (PAGINATION_PARAMS.some((param) => query.has(param))) return;
  // eslint-disable-next-line no-console
  console.warn(
    `[api] liste tronquée : ${paged.items.length} éléments reçus sur ${paged.count} — ${response.url}`,
  );
}
